import { useEffect, useState } from "react";
import { Coins, TrendingUp } from "lucide-react";
import { cardBase, cardPrimary, cardSuccess } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { listBuildings } from "@/lib/api/building";
import {
  getLatestRealizedSavings,
  type RealizedSavingsReport,
} from "@/lib/api/realized-savings";

/**
 * PotentialVsRealizedCard — Säästöpotentiaali (backward-sim, koko historiasta)
 * vs. Toteutunut säästö (mitattu M&V, data_context-jaksot). Strategia-tason
 * ydinlukema: paljonko kanta voisi tuottaa vs. paljonko se tuottaa nyt.
 *
 * Potentiaali tulee building-listan kätketystä KPI-kentästä (jo laskettu palvelimella,
 * story #65). Toteutunut haetaan kohteittain realized-savings-reports -taulusta (#295);
 * `null` tarkoittaa "ei vielä laskettu" (ei baseline+operational-jaksoparia) — eri asia
 * kuin laskettu nollasäästö.
 */
type PortfolioBuilding = {
  id: string;
  name: string;
  potentialEurPerMonth: number;
  /** null = not yet computed (distinct from a computed zero). */
  realizedEurPerMonth: number | null;
};

const MS_PER_DAY = 86_400_000;

/** Realized savings is a total over the operational period, not a monthly figure —
 * normalize it to €/kk the same way the potential simulator does, so the two
 * numbers are comparable. */
function monthlyEur(report: RealizedSavingsReport): number {
  const start = new Date(report.operational_period_start).getTime();
  const end = new Date(report.operational_period_end).getTime();
  const days = (end - start) / MS_PER_DAY;
  return days > 0 ? (report.total_eur / days) * 30 : 0;
}

export function PotentialVsRealizedCard() {
  const [rows, setRows] = useState<PortfolioBuilding[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listBuildings()
      .then(async (buildings) => {
        const realized = await Promise.all(
          buildings.map((b) => getLatestRealizedSavings(b.id)),
        );
        if (cancelled) return;
        setRows(
          buildings.map((b, i) => ({
            id: b.id,
            name: b.name,
            potentialEurPerMonth: b.optimization_potential_eur_month ?? 0,
            realizedEurPerMonth: realized[i] ? monthlyEur(realized[i]) : null,
          })),
        );
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Virhe");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <section className={cn(cardBase, "p-4 sm:p-5 text-sm text-destructive")}>
        Säästöpotentiaalia ei voitu ladata: {error}
      </section>
    );
  }
  if (!rows) {
    return (
      <section
        className={cn(cardBase, "p-4 sm:p-5 text-sm text-muted-foreground")}
      >
        Ladataan säästöpotentiaalia…
      </section>
    );
  }

  const computed = rows.filter(
    (r) => r.realizedEurPerMonth !== null,
  ) as (PortfolioBuilding & { realizedEurPerMonth: number })[];
  const pendingCount = rows.length - computed.length;

  const potential = computed.reduce((a, b) => a + b.potentialEurPerMonth, 0);
  const realized = computed.reduce((a, b) => a + b.realizedEurPerMonth, 0);
  const captureRate =
    potential > 0 ? Math.round((realized / potential) * 100) : 0;
  const gap = potential - realized;

  const top = [...computed]
    .map((b) => ({ b, gap: b.potentialEurPerMonth - b.realizedEurPerMonth }))
    .sort((a, z) => z.gap - a.gap)
    .slice(0, 3);

  if (computed.length === 0) {
    return (
      <section className={cn(cardBase, "p-4 sm:p-5")}>
        <header className="flex items-center gap-2 mb-2">
          <div className="h-7 w-7 rounded-md bg-surface-elevated grid place-items-center text-primary">
            <Coins className="h-3.5 w-3.5" />
          </div>
          <h2 className="text-sm font-semibold tracking-tight">
            Säästöpotentiaali vs. toteutunut
          </h2>
        </header>
        <p className="text-xs text-muted-foreground">
          Toteutunutta säästöä ei ole vielä laskettu — yhdelläkään kohteella ei
          ole sekä baseline- että operational-jaksoa merkittynä.
        </p>
      </section>
    );
  }

  return (
    <section className={cn(cardBase, "p-4 sm:p-5")}>
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-7 w-7 rounded-md bg-surface-elevated grid place-items-center text-primary">
            <Coins className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold tracking-tight">
              Säästöpotentiaali vs. toteutunut
            </h2>
            <p className="text-[11px] text-muted-foreground">
              Backward-sim omalla historialla · mitattu M&V (baseline vs.
              operational)
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-success">
          <TrendingUp className="h-3 w-3" /> Capture {captureRate} %
        </span>
      </header>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className={cn(cardPrimary, "p-3")}>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            Potentiaali
          </div>
          <div className="mt-1 text-2xl font-display tracking-tight text-primary">
            {potential.toLocaleString("fi-FI")} €/kk
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            Mitä kanta voisi tuottaa
          </div>
        </div>
        <div className={cn(cardSuccess, "p-3")}>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            Toteutunut
          </div>
          <div className="mt-1 text-2xl font-display tracking-tight text-success">
            {realized.toLocaleString("fi-FI")} €/kk
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            Mitattu M&V · normalisoitu €/kk
          </div>
        </div>
      </div>

      {/* Capture-palkki */}
      <div className="mb-3">
        <div className="h-2 rounded-full bg-background overflow-hidden relative border border-border">
          <div
            className="h-full bg-success"
            style={{ width: `${captureRate}%` }}
          />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>Kiinniotettu {captureRate} %</span>
          <span className="text-warning">
            Jäljellä {gap.toLocaleString("fi-FI")} €/kk
          </span>
        </div>
      </div>

      <div className="border-t border-border/60 pt-3">
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">
          Suurin kiinniottamaton potentiaali
        </div>
        <ul className="space-y-1.5">
          {top.map(({ b, gap }) => (
            <li
              key={b.id}
              className="flex items-center justify-between text-xs"
            >
              <span className="truncate text-foreground/90">{b.name}</span>
              <span className="font-mono text-warning shrink-0 ml-2">
                +{gap.toLocaleString("fi-FI")} €/kk
              </span>
            </li>
          ))}
        </ul>
      </div>

      {pendingCount > 0 && (
        <p className="mt-3 text-[10px] text-muted-foreground border-t border-border/60 pt-2">
          {pendingCount} kohdetta odottaa mittausjaksoa (ei baseline+operational
          -jaksoparia vielä) — ei mukana yllä olevissa luvuissa.
        </p>
      )}
    </section>
  );
}
